import type { TriangleFormat, TriangleAngle, TriangleScalarProperty } from '../elements/shapes/triangle/triangle.model';
import type { SolvedTriangle } from './solver.model';

const DEG_TO_RAD = Math.PI / 180;
const EPSILON = 1e-9;

/** Angle keys used in solver */
const ANGLE_KEYS: readonly TriangleAngle[] = ['alpha', 'beta', 'gamma'] as const;
const SIDE_KEYS = ['a', 'b', 'c'] as const;

/** Map exterior angle keys to their interior equivalents */
const EXT_TO_INTERIOR: Record<string, TriangleAngle> = {
  extAlpha: 'alpha',
  extBeta: 'beta',
  extGamma: 'gamma',
};

/**
 * Preprocess values: convert exterior angle keys (extAlpha/extBeta/extGamma) to interior equivalents.
 * Exterior angle value in degrees is converted: interior = 180 - exterior.
 */
function preprocessExteriorAngles(
  values: Partial<Record<TriangleScalarProperty, number>>,
): Partial<Record<TriangleScalarProperty, number>> {
  const result: Partial<Record<TriangleScalarProperty, number>> = {};
  for (const [key, val] of Object.entries(values)) {
    const interiorKey = EXT_TO_INTERIOR[key];
    if (interiorKey) {
      result[interiorKey] = 180 - val;
    } else {
      result[key as TriangleScalarProperty] = val;
    }
  }
  return result;
}

/**
 * Valid value key sets per triangle format.
 */
const VALID_SETS: Record<TriangleFormat, string[][]> = {
  SSS: [['a', 'b', 'c']],
  SAS: [
    ['a', 'b', 'gamma'],
    ['b', 'c', 'alpha'],
    ['c', 'a', 'beta'],
  ],
  ASA: [
    ['alpha', 'beta', 'c'],
    ['beta', 'gamma', 'a'],
    ['gamma', 'alpha', 'b'],
  ],
  AAS: [
    ['alpha', 'beta', 'a'],
    ['alpha', 'beta', 'b'],
    ['beta', 'gamma', 'b'],
    ['beta', 'gamma', 'c'],
    ['gamma', 'alpha', 'c'],
    ['gamma', 'alpha', 'a'],
  ],
  RHS: [
    ['alpha', 'a', 'b'],
    ['alpha', 'a', 'c'],
    ['beta', 'b', 'a'],
    ['beta', 'b', 'c'],
    ['gamma', 'c', 'a'],
    ['gamma', 'c', 'b'],
  ],
};

/**
 * SAS combos: which angle is "included" between which two sides.
 * alpha at vertex A → between sides b and c
 * beta  at vertex B → between sides a and c
 * gamma at vertex C → between sides a and b
 */
const SAS_COMBOS = [
  { sides: ['b', 'c'] as const, angle: 'alpha' as const },
  { sides: ['a', 'c'] as const, angle: 'beta' as const },
  { sides: ['a', 'b'] as const, angle: 'gamma' as const },
];

/**
 * ASA combos: which side is "included" between which two angles.
 * c (AB) between alpha (at A) and beta (at B)
 * a (BC) between beta (at B) and gamma (at C)
 * b (CA) between gamma (at C) and alpha (at A)
 */
const ASA_COMBOS = [
  { angles: ['alpha', 'beta'] as const, side: 'c' as const },
  { angles: ['beta', 'gamma'] as const, side: 'a' as const },
  { angles: ['gamma', 'alpha'] as const, side: 'b' as const },
];

/** Map angle key to opposite side key */
const ANGLE_TO_OPP_SIDE: Record<TriangleAngle, 'a' | 'b' | 'c'> = {
  alpha: 'a',
  beta: 'b',
  gamma: 'c',
};

/**
 * Validate that the provided value keys match a valid set for the format.
 */
export function validateTriangleValues(
  format: TriangleFormat,
  valueKeys: string[],
): { valid: boolean; error?: string } {
  const validSets = VALID_SETS[format];
  // Normalize exterior angle keys to interior equivalents for validation
  const normalizedKeys = valueKeys.map((k) => EXT_TO_INTERIOR[k] ?? k);
  const sortedKeys = [...normalizedKeys].sort();

  const match = validSets.some((set) => {
    const sortedSet = [...set].sort();
    return sortedSet.length === sortedKeys.length && sortedSet.every((k, i) => k === sortedKeys[i]);
  });

  if (!match) {
    return {
      valid: false,
      error: `Invalid value keys [${valueKeys.join(', ')}] for format ${format}. Valid sets: ${validSets
        .map((s) => `[${s.join(', ')}]`)
        .join(', ')}`,
    };
  }

  return { valid: true };
}

/**
 * Auto-detect triangle format from available scalar keys.
 */
export function detectTriangleFormat(values: Partial<Record<string, number>>): TriangleFormat {
  const hasSide = (k: string) => values[k] !== undefined;
  const hasAngle = (k: string) => values[k] !== undefined;

  const sideCount = SIDE_KEYS.filter(hasSide).length;
  const angleCount = ANGLE_KEYS.filter(hasAngle).length;

  // SSS: 3 sides, 0 angles
  if (sideCount === 3 && angleCount === 0) {
    return 'SSS';
  }

  // RHS: one 90° angle + hypotenuse + one other side
  if (angleCount >= 1 && sideCount >= 2) {
    const rightAngle = ANGLE_KEYS.find((k) => values[k] !== undefined && Math.abs(values[k]! - 90) < EPSILON);
    if (rightAngle) {
      const oppSide = ANGLE_TO_OPP_SIDE[rightAngle];
      if (values[oppSide] !== undefined) {
        return 'RHS';
      }
    }
  }

  // SAS: 2 sides + 1 included angle
  if (sideCount === 2 && angleCount === 1) {
    const givenAngle = ANGLE_KEYS.find(hasAngle)!;
    const isSAS = SAS_COMBOS.some(
      (combo) => combo.angle === givenAngle && combo.sides.every((s) => values[s] !== undefined),
    );
    if (isSAS) {
      return 'SAS';
    }
  }

  // ASA: 2 angles + 1 included side
  if (angleCount === 2 && sideCount === 1) {
    const givenSide = SIDE_KEYS.find(hasSide)!;
    const isASA = ASA_COMBOS.some(
      (combo) => combo.side === givenSide && combo.angles.every((a) => values[a] !== undefined),
    );
    if (isASA) {
      return 'ASA';
    }
  }

  // AAS: 2 angles + 1 non-included side
  if (angleCount === 2 && sideCount === 1) {
    return 'AAS';
  }

  throw new Error(
    `Cannot detect triangle format from keys: sides=[${SIDE_KEYS.filter(hasSide).join(
      ',',
    )}], angles=[${ANGLE_KEYS.filter(hasAngle).join(',')}]`,
  );
}

/**
 * Solve a triangle given numeric values. Format is auto-detected if not provided.
 * Returns all sides, angles, vertices, area, and perimeter.
 *
 * Angles in values are expected in degrees, returned angles are in radians.
 */
export function solveTriangle(
  values: Partial<Record<TriangleScalarProperty, number>>,
  format?: TriangleFormat,
): SolvedTriangle {
  // Preprocess exterior angle keys to interior equivalents
  const normalizedValues = preprocessExteriorAngles(values);

  // Auto-detect format if not provided
  const resolvedFormat = format ?? detectTriangleFormat(normalizedValues);

  let a: number, b: number, c: number;
  let alpha: number, beta: number, gamma: number; // radians

  switch (resolvedFormat) {
    case 'SSS':
      ({ a, b, c } = normalizedValues as { a: number; b: number; c: number });
      validateTriangleInequality(a, b, c);
      alpha = lawOfCosinesAngle(b, c, a);
      beta = lawOfCosinesAngle(a, c, b);
      gamma = Math.PI - alpha - beta;
      break;

    case 'SAS': {
      const sasResult = solveSAS(normalizedValues);
      ({ a, b, c, alpha, beta, gamma } = sasResult);
      break;
    }

    case 'ASA': {
      const asaResult = solveASA(normalizedValues);
      ({ a, b, c, alpha, beta, gamma } = asaResult);
      break;
    }

    case 'AAS': {
      const aasResult = solveAAS(normalizedValues);
      ({ a, b, c, alpha, beta, gamma } = aasResult);
      break;
    }

    case 'RHS': {
      const rhsResult = solveRHS(normalizedValues);
      ({ a, b, c, alpha, beta, gamma } = rhsResult);
      break;
    }

    default:
      throw new Error(`Unknown triangle format: ${resolvedFormat as string}`);
  }

  const vertices = computeTriangleVertices(a, b, c, alpha, beta, gamma, resolvedFormat, normalizedValues);
  const s = (a + b + c) / 2;
  const area = Math.sqrt(s * (s - a) * (s - b) * (s - c));

  return {
    vertices,
    sides: { a, b, c },
    angles: { alpha, beta, gamma },
    exteriorAngles: {
      extAlpha: Math.PI - alpha,
      extBeta: Math.PI - beta,
      extGamma: Math.PI - gamma,
    },
    area,
    perimeter: a + b + c,
  };
}

// ---------------------------------------------------------------------------
// Format-specific solvers
// ---------------------------------------------------------------------------

function solveSAS(values: Partial<Record<TriangleScalarProperty, number>>) {
  let a: number, b: number, c: number, alpha: number, beta: number, gamma: number;

  if (values.a !== undefined && values.b !== undefined && values.gamma !== undefined) {
    a = values.a;
    b = values.b;
    gamma = values.gamma * DEG_TO_RAD;
    c = lawOfCosinesSide(a, b, gamma);
    alpha = lawOfCosinesAngle(b, c, a);
    beta = Math.PI - alpha - gamma;
  } else if (values.b !== undefined && values.c !== undefined && values.alpha !== undefined) {
    b = values.b;
    c = values.c;
    alpha = values.alpha * DEG_TO_RAD;
    a = lawOfCosinesSide(b, c, alpha);
    beta = lawOfCosinesAngle(a, c, b);
    gamma = Math.PI - alpha - beta;
  } else if (values.c !== undefined && values.a !== undefined && values.beta !== undefined) {
    c = values.c;
    a = values.a;
    beta = values.beta * DEG_TO_RAD;
    b = lawOfCosinesSide(c, a, beta);
    alpha = lawOfCosinesAngle(b, c, a);
    gamma = Math.PI - alpha - beta;
  } else {
    throw new Error('Invalid SAS values');
  }

  return { a, b, c, alpha, beta, gamma };
}

function solveASA(values: Partial<Record<TriangleScalarProperty, number>>) {
  let a: number, b: number, c: number, alpha: number, beta: number, gamma: number;

  if (values.alpha !== undefined && values.beta !== undefined && values.c !== undefined) {
    alpha = values.alpha * DEG_TO_RAD;
    beta = values.beta * DEG_TO_RAD;
    c = values.c;
    gamma = Math.PI - alpha - beta;
    a = lawOfSinesSide(c, gamma, alpha);
    b = lawOfSinesSide(c, gamma, beta);
  } else if (values.beta !== undefined && values.gamma !== undefined && values.a !== undefined) {
    beta = values.beta * DEG_TO_RAD;
    gamma = values.gamma * DEG_TO_RAD;
    a = values.a;
    alpha = Math.PI - beta - gamma;
    b = lawOfSinesSide(a, alpha, beta);
    c = lawOfSinesSide(a, alpha, gamma);
  } else if (values.gamma !== undefined && values.alpha !== undefined && values.b !== undefined) {
    gamma = values.gamma * DEG_TO_RAD;
    alpha = values.alpha * DEG_TO_RAD;
    b = values.b;
    beta = Math.PI - gamma - alpha;
    a = lawOfSinesSide(b, beta, alpha);
    c = lawOfSinesSide(b, beta, gamma);
  } else {
    throw new Error('Invalid ASA values');
  }

  return { a, b, c, alpha, beta, gamma };
}

function solveAAS(values: Partial<Record<TriangleScalarProperty, number>>) {
  // Two angles are given, compute the third
  const angleKeys = ANGLE_KEYS.filter((k) => values[k] !== undefined);
  const sideKeys = SIDE_KEYS.filter((k) => values[k] !== undefined);

  if (angleKeys.length !== 2 || sideKeys.length !== 1) {
    throw new Error('AAS requires exactly 2 angles and 1 side');
  }

  // Assign known angles (convert to radians)
  const angles: Record<string, number> = {};
  for (const k of angleKeys) {
    angles[k] = values[k]! * DEG_TO_RAD;
  }

  // Compute missing angle
  const missingAngle = ANGLE_KEYS.find((k) => angles[k] === undefined)!;
  angles[missingAngle] = Math.PI - angles[angleKeys[0]] - angles[angleKeys[1]];

  const alpha = angles['alpha'];
  const beta = angles['beta'];
  const gamma = angles['gamma'];

  // Known side
  const knownSide = sideKeys[0];
  const knownSideValue = values[knownSide]!;

  // Compute all sides via law of sines
  const sideAngleMap: Record<string, number> = { a: alpha, b: beta, c: gamma };
  const knownAngle = sideAngleMap[knownSide];

  const a = knownSide === 'a' ? knownSideValue : lawOfSinesSide(knownSideValue, knownAngle, alpha);
  const b = knownSide === 'b' ? knownSideValue : lawOfSinesSide(knownSideValue, knownAngle, beta);
  const c = knownSide === 'c' ? knownSideValue : lawOfSinesSide(knownSideValue, knownAngle, gamma);

  return { a, b, c, alpha, beta, gamma };
}

function solveRHS(values: Partial<Record<TriangleScalarProperty, number>>) {
  // Identify right angle
  const rightAngleKey = ANGLE_KEYS.find((k) => values[k] !== undefined && Math.abs(values[k]! - 90) < EPSILON);

  if (!rightAngleKey) {
    throw new Error('RHS requires one angle to be 90 degrees');
  }

  // The side opposite the right angle is the hypotenuse
  const hypSide = ANGLE_TO_OPP_SIDE[rightAngleKey];
  const otherSides = SIDE_KEYS.filter((s) => s !== hypSide);
  const givenSides = otherSides.filter((s) => values[s] !== undefined);

  if (values[hypSide] === undefined) {
    throw new Error('RHS requires the hypotenuse value');
  }

  const hyp = values[hypSide]!;

  if (givenSides.length !== 1) {
    throw new Error('RHS requires exactly the hypotenuse and one leg');
  }

  const leg1 = values[givenSides[0]]!;
  const leg2 = Math.sqrt(hyp * hyp - leg1 * leg1);

  // Assign sides
  const sides: Record<string, number> = { [hypSide]: hyp };
  sides[givenSides[0]] = leg1;
  const otherLeg = otherSides.find((s) => s !== givenSides[0])!;
  sides[otherLeg] = leg2;

  const a = sides['a'];
  const b = sides['b'];
  const c = sides['c'];

  // Compute angles
  const rightAngleRad = Math.PI / 2;
  const anglesMap: Record<string, number> = { [rightAngleKey]: rightAngleRad };

  // Use asin for the other angles
  for (const side of SIDE_KEYS) {
    const angleName = { a: 'alpha', b: 'beta', c: 'gamma' }[side] as string;
    if (anglesMap[angleName] === undefined) {
      anglesMap[angleName] = Math.asin(sides[side] / sides[hypSide]);
    }
  }

  const alpha = anglesMap['alpha'];
  const beta = anglesMap['beta'];
  const gamma = anglesMap['gamma'];

  return { a, b, c, alpha, beta, gamma };
}

// ---------------------------------------------------------------------------
// Vertex computation (canonical placement)
// ---------------------------------------------------------------------------

function computeTriangleVertices(
  a: number,
  b: number,
  c: number,
  alpha: number,
  _beta: number,
  _gamma: number,
  format: TriangleFormat,
  values: Partial<Record<TriangleScalarProperty, number>>,
): SolvedTriangle['vertices'] {
  // RHS: right-angle vertex at origin, legs along axes
  if (format === 'RHS') {
    return computeRHSVertices(a, b, c, values);
  }

  // Default canonical frame: A at origin, side c (AB) along x-axis, C above
  const Ax = 0;
  const Ay = 0;
  const Bx = c;
  const By = 0;

  // C computed from A using side b and angle alpha
  const Cx = b * Math.cos(alpha);
  const Cy = b * Math.sin(alpha); // always positive (above x-axis)

  return {
    A: [Ax, Ay],
    B: [Bx, By],
    C: [Cx, Cy],
  };
}

function computeRHSVertices(
  a: number,
  b: number,
  c: number,
  values: Partial<Record<TriangleScalarProperty, number>>,
): SolvedTriangle['vertices'] {
  const rightAngleKey = ANGLE_KEYS.find((k) => values[k] !== undefined && Math.abs(values[k]! - 90) < EPSILON)!;

  // Map angle key to vertex name
  const angleToVertex: Record<TriangleAngle, 'A' | 'B' | 'C'> = {
    alpha: 'A',
    beta: 'B',
    gamma: 'C',
  };
  const rightAngleVertex = angleToVertex[rightAngleKey];

  // Right-angle vertex at origin, legs along axes
  const sides: Record<string, number> = { a, b, c };
  const hypSide = ANGLE_TO_OPP_SIDE[rightAngleKey];

  // The two legs (sides not opposite to the right angle)
  const legSides = SIDE_KEYS.filter((s) => s !== hypSide);
  const leg1 = sides[legSides[0]];
  const leg2 = sides[legSides[1]];

  // Determine vertex assignments based on which vertex has the right angle
  switch (rightAngleVertex) {
    case 'C':
      return { C: [0, 0], A: [leg1, 0], B: [0, leg2] };
    case 'A':
      return { A: [0, 0], B: [leg1, 0], C: [0, leg2] };
    case 'B':
      return { B: [0, 0], C: [leg1, 0], A: [0, leg2] };
    default:
      throw new Error(`Invalid right angle vertex: ${rightAngleVertex as string}`);
  }
}

// ---------------------------------------------------------------------------
// Math helpers
// ---------------------------------------------------------------------------

/** Law of cosines: compute side opposite to angle C given sides a, b and angle C */
function lawOfCosinesSide(a: number, b: number, C: number): number {
  return Math.sqrt(a * a + b * b - 2 * a * b * Math.cos(C));
}

/** Law of cosines: compute angle opposite to side a given sides b, c and side a */
function lawOfCosinesAngle(b: number, c: number, a: number): number {
  const cosA = (b * b + c * c - a * a) / (2 * b * c);
  return Math.acos(Math.max(-1, Math.min(1, cosA))); // clamp for floating point
}

/** Law of sines: compute side opposite to angle A given known side/angle pair */
function lawOfSinesSide(knownSide: number, knownAngle: number, targetAngle: number): number {
  return (knownSide * Math.sin(targetAngle)) / Math.sin(knownAngle);
}

function validateTriangleInequality(a: number, b: number, c: number): void {
  if (a + b <= c || b + c <= a || c + a <= b) {
    throw new Error(`Triangle inequality violated: sides ${a}, ${b}, ${c} cannot form a triangle`);
  }
  if (a <= 0 || b <= 0 || c <= 0) {
    throw new Error(`All sides must be positive: ${a}, ${b}, ${c}`);
  }
}
